/**
 * ValoraaBead wishlist (saved on this device).
 *
 * One list of product handles in localStorage ('vb:wishlist', newest last), shared by:
 * - every heart button: `[data-vb-wish="<handle>"]` (product cards, the product page buy row);
 *   a click toggles the handle, every heart for that product repaints (aria-pressed), and a
 *   polite status line ([data-vb-wish-status] in the header, or the buy row's own
 *   [data-vb-wish-live]) says "Saved" / "Removed",
 * - count badges: `[data-vb-wish-count]` (header heart, phone menu link) show the number of saved
 *   pieces and hide at 0,
 * - the wishlist page (sections/vb-wishlist.liquid) listens for `vb:wishlist:change`.
 * The price at save time is kept per handle ('vb:wishlist:prices', from the heart's data-price,
 * in cents), so the wishlist page can say "Now ₹X (was ₹Y when you saved it)" only when the
 * price really dropped. Saving shows a small toast ("Saved to your wishlist · View").
 * Other tabs stay in sync through the storage event. Without storage (private mode, blocked) the
 * hearts still toggle for this visit.
 * Loaded once by sections/vb-header.liquid (defer).
 */
(() => {
  if (window.vbWishlist) return;

  const KEY = 'vb:wishlist';
  const PRICES = 'vb:wishlist:prices';
  let memory = [];

  /** @returns {Record<string, number>} */
  const readPrices = () => {
    try {
      const map = JSON.parse(localStorage.getItem(PRICES) || '{}');
      return map && typeof map === 'object' ? map : {};
    } catch {
      return {};
    }
  };
  const writePrice = (/** @type {string} */ handle, /** @type {number | null} */ cents) => {
    try {
      const map = readPrices();
      if (cents === null) delete map[handle];
      else map[handle] = cents;
      localStorage.setItem(PRICES, JSON.stringify(map));
    } catch {
      /* storage blocked */
    }
  };

  /** @returns {string[]} */
  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      memory = Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      /* storage blocked: keep the in-memory list */
    }
    return [...memory];
  };

  /** @param {string[]} list */
  const write = (list) => {
    memory = [...new Set(list)];
    try {
      localStorage.setItem(KEY, JSON.stringify(memory));
    } catch {
      /* storage blocked: the list lives for this visit only */
    }
    paint();
    document.dispatchEvent(new CustomEvent('vb:wishlist:change', { detail: { list: [...memory] } }));
  };

  const has = (/** @type {string} */ handle) => read().includes(handle);

  const toggle = (/** @type {string} */ handle) => {
    const list = read();
    const on = !list.includes(handle);
    write(on ? [...list, handle] : list.filter((h) => h !== handle));
    return on;
  };

  const remove = (/** @type {string} */ handle) => write(read().filter((h) => h !== handle));

  /** Adds handles (e.g. a shared list) without duplicates, keeping the existing order. */
  const addMany = (/** @type {string[]} */ handles) => write([...read(), ...handles.filter((h) => !read().includes(h))]);

  /** Re-inserts a handle at a position (Undo on the wishlist page). */
  const insertAt = (/** @type {string} */ handle, /** @type {number} */ index) => {
    const list = read().filter((h) => h !== handle);
    list.splice(Math.max(0, Math.min(index, list.length)), 0, handle);
    write(list);
  };

  /* ---------- Toast: "Saved to your wishlist · View" ---------- */
  let toast = null;
  let toastTimer = 0;
  const showToast = () => {
    const status = document.querySelector('[data-vb-wish-status]');
    const url = status?.getAttribute('data-url');
    if (!status) return;
    if (!toast?.isConnected) {
      toast = document.createElement('div');
      toast.className = 'vb-wish-toast';
      toast.setAttribute('aria-hidden', 'true');
      document.body.append(toast);
    }
    toast.replaceChildren();
    const text = document.createElement('span');
    text.textContent = status.getAttribute('data-toast') || '';
    toast.append(text);
    if (url && !window.location.pathname.endsWith(new URL(url, window.location.href).pathname)) {
      const link = document.createElement('a');
      link.href = url;
      link.textContent = status.getAttribute('data-view') || '';
      link.tabIndex = -1;
      toast.append(link);
    }
    toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast?.classList.remove('is-on'), 3600);
  };

  /** Repaint every heart and count on the page (or inside `root`). */
  const paint = (root = document) => {
    const list = read();
    for (const button of root.querySelectorAll('[data-vb-wish]')) {
      const on = list.includes(button.getAttribute('data-vb-wish') || '');
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? button.getAttribute('data-label-on') : button.getAttribute('data-label-off');
      if (label) button.setAttribute('aria-label', label);
    }
    for (const badge of root.querySelectorAll('[data-vb-wish-count]')) {
      badge.textContent = String(list.length);
      badge.hidden = list.length === 0;
    }
    for (const label of root.querySelectorAll('[data-vb-wish-count-label]')) {
      const one = label.getAttribute('data-one') || '';
      const other = label.getAttribute('data-other') || '';
      label.textContent = list.length === 0 ? '' : (list.length === 1 ? one : other).replace('[count]', String(list.length));
    }
  };

  /** @param {Element} button @param {boolean} on */
  const announce = (button, on) => {
    const local = button.closest('[data-vb-buy]')?.querySelector('[data-vb-wish-live]');
    const status = local || document.querySelector('[data-vb-wish-status]');
    if (!status) return;
    const text =
      (on ? button.getAttribute('data-live-on') : button.getAttribute('data-live-off')) ||
      (on ? status.getAttribute('data-on') : status.getAttribute('data-off')) ||
      '';
    status.textContent = '';
    requestAnimationFrame(() => (status.textContent = text));
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-vb-wish]') : null;
    if (!target) return;
    event.preventDefault();
    const handle = target.getAttribute('data-vb-wish') || '';
    const on = toggle(handle);
    const cents = Number(target.getAttribute('data-price'));
    writePrice(handle, on && Number.isFinite(cents) && cents > 0 ? cents : null);
    announce(target, on);
    if (on) showToast();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    paint();
    document.dispatchEvent(new CustomEvent('vb:wishlist:change', { detail: { list: read() } }));
  });

  // Sections re-rendered by the theme (filters, load more, cart, editor) bring new hearts
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element && (node.matches('[data-vb-wish], [data-vb-wish-count]') || node.querySelector('[data-vb-wish], [data-vb-wish-count]'))) {
          paint(node.parentElement || document);
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.vbWishlist = { read, write, has, toggle, remove, paint, addMany, insertAt, readPrices, writePrice, showToast };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => paint(), { once: true });
  } else {
    paint();
  }
  window.addEventListener('pageshow', () => paint());
})();
